import type { IDocumentService, IScope, ITimeoutService } from "angular";
import {} from "ng-file-upload";
import { finalize } from "rxjs/operators";

import { cloneDeep, isEmpty, unset } from "lodash";
import type { IProfileService } from "App/Modules/Auth/Services/Profile/IProfileService";

import type { ICoverageCalculationsService } from "App/Modules/Gui";
import type { IMap, IPoint, IResponseWithLoading } from "App/Modules/Models";
import type { ISitesRepository } from "App/Modules/Repositories";
import { PatchDelta } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";
import type { IConfig } from "App/Modules/Utils";

export default class EditMapComponentController {
    public static $inject: string[] = [
        "$scope",
        "$timeout",
        "$document",
        "mmProfileService",
        "mmSitesRepository",
        "uiGmapMapScriptLoader",
        "irConfig",
        "Upload",
        "mmCoverageCalculationsService",
        "uiGmapGoogleMapApi",
    ];

    public siteId: string;

    public map: IResponseWithLoading<IMap>;

    public baseUrl: string;

    public googleMap: any;

    public activeMapTab: number;

    public fileToUpload: any;

    public readonly EXPORTEDWIDTH: number = 640;

    public readonly EXPORTEDHEIGHT: number = 640;

    public svgRoot: SVGSVGElement;

    public editedMap: IMap;

    public editScaleResponse: IResponseWithLoading<IMap>;

    public relativeScaleCoordinates: IPoint[];

    public scaleEditing: boolean;

    public mapChanged?: () => void;

    constructor(
        private readonly $scope: IScope,
        private readonly $timeout: ITimeoutService,
        private readonly $document: IDocumentService,
        private readonly profileService: IProfileService,
        private readonly sitesRepository: ISitesRepository,
        private readonly uiGmapMapScriptLoader: any,
        private readonly config: IConfig,
        private readonly upload: ng.angularFileUpload.IUploadService,
        private readonly coverageCalculationsService: ICoverageCalculationsService,
        private readonly uiGmapGoogleMapApi: any,
    ) {}

    public $onInit() {
        this.scaleEditing = true;
        this.baseUrl = this.map.data.image.url;
        this.editedMap = cloneDeep(this.map.data);

        this.loadGoogleMaps();
        this.setCachebustingMapUrl();

        this.$scope.$watch("$ctrl.fileToUpload", () => {
            this.uploadMap(this.fileToUpload);
        });
    }

    public loadGoogleMaps() {
        if (this.uiGmapMapScriptLoader.isGoogleMapsLoaded) {
            unset(window, "google.maps");
        }

        const googleMapsOptions = {
            china: false,
            isGoogleMapsForWork: false,
            key: this.config.googleMapsApiKey,
            language: this.profileService.getProfileIfAvailable().language.key,
            libraries: "places,geometry",
            preventLoad: false,
            transport: "https",
            v: "3.37",
        };

        this.uiGmapMapScriptLoader.load(googleMapsOptions).then(() => {
            this.googleMapsLoaded();
        });
    }

    public getScaleCoordinates(): IPoint[] {
        return this.editedMap.scaleCoordinates;
    }

    public uploadMap(file: any) {
        this.relativeScaleCoordinates = cloneDeep(this.editedMap.scaleCoordinates);
        this.coverageCalculationsService.polygonToNormalized(this.relativeScaleCoordinates, this.map.data.image);

        if (file) {
            file.upload = this.upload.upload({
                data: { file },
                method: "PUT",
                url: this.baseUrl,
            } as ng.angularFileUpload.IFileUploadConfigFile);

            file.upload.then(
                () => {
                    this.setCachebustingMapUrl();
                    this.activeMapTab = 0;
                    this.mapChanged();

                    extendWithLoadingAndFail(this.sitesRepository.getMap(this.siteId))
                        .pipe(finalize(() => this.$scope.$applyAsync()))
                        .subscribe(
                            (response) => {
                                this.scaleEditing = false;
                                if (!response.hasError && response.data) {
                                    this.map = response;
                                    this.editedMap = cloneDeep(this.map.data);
                                    this.coverageCalculationsService.polygonToAbsolute(
                                        this.relativeScaleCoordinates,
                                        this.map.data.image,
                                    );
                                    this.editedMap.scaleCoordinates = cloneDeep(this.relativeScaleCoordinates);
                                    this.scaleEditing = true;
                                }
                            },
                            (error) => {
                                this.map = error;
                            },
                        );
                },
                (response: any) => {
                    if (response.status > 0) {
                        throw new Error(`Upload failed. Status: ${response.status} Data: ${response.data}`);
                    }
                },
                (evt: any) => {
                    file.progress = Math.min(100, (100 * evt.loaded) / evt.total);
                },
            );
        }
    }

    public saveGoogleMapAsImage(keepLabels: boolean) {
        const { gMap } = this.googleMap;

        let w = gMap.getDiv().offsetWidth;
        let h = gMap.getDiv().offsetHeight;
        let zoom = gMap.getZoom();
        const scale = 1;

        while (w > 640 || h > 640) {
            w /= 2;
            h /= 2;
            zoom--;
        }
        while (w < 320 && h < 320) {
            w *= 2;
            h *= 2;
            zoom++;
        }

        const mapTypeId = gMap.getMapTypeId();

        const { spherical } = google.maps.geometry;
        const bounds = gMap.getBounds();
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const nw = new google.maps.LatLng(ne.lat(), sw.lng());
        const widthInMeters = spherical.computeDistanceBetween(nw, ne);

        this.sitesRepository
            .putMap(this.siteId, {
                center: this.googleMap.map.center,
                height: this.EXPORTEDHEIGHT,
                mapTypeId,
                scale,
                style: keepLabels ? null : "feature:poi|visibility:off",
                width: this.EXPORTEDWIDTH,
                widthInMeters,
                zoom,
            })
            .pipe(
                finalize(() => {
                    this.editedMap.scaleValue = Math.round(widthInMeters);
                    const coordinates: IPoint[] = [
                        { x: 0, y: this.EXPORTEDWIDTH / 2 },
                        { x: this.EXPORTEDHEIGHT, y: this.EXPORTEDWIDTH / 2 },
                    ];
                    this.editedMap.scaleCoordinates = coordinates;

                    this.saveScale();
                }),
            )
            .subscribe(() => {
                this.setCachebustingMapUrl();
                this.activeMapTab = 0;
            });
    }

    public saveScale() {
        const patchObject = PatchDelta.difference(this.map.data, this.editedMap);

        if (patchObject.scaleCoordinates) {
            this.coverageCalculationsService.polygonToNormalized(patchObject.scaleCoordinates, this.map.data.image);
        }

        if (!isEmpty(patchObject)) {
            extendWithLoadingAndFail(this.sitesRepository.patchMap(this.siteId, patchObject))
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (response) => {
                        this.editScaleResponse = response;
                        if (!response.isLoading && response.data) {
                            this.map.data.scaleCoordinates = response.data.scaleCoordinates;
                            this.map.data.scaleValue = response.data.scaleValue;
                        }
                    },
                    (error) => {
                        this.editScaleResponse = error;
                    },
                );
        }
    }

    public setCachebustingMapUrl() {
        const now = new Date();
        // Must make a new image reference so that the mm-svg-image-canvas
        // can detect change
        this.map.data.image = {
            ...this.map.data.image,
            url: `${this.baseUrl}/?reloadTimestamp=${now.toISOString()}`,
        };
    }

    private googleMapsLoaded() {
        this.googleMap = {
            control: {},
            gMap: null,
            map: {
                center: {
                    latitude: 58.7671076,
                    longitude: 12.9254696,
                },
                zoom: 6,
            },
            options: {
                center: {
                    lat: 58.7671076,
                    lng: 12.9254696,
                },
                mapTypeControlOptions: {
                    position: google.maps.ControlPosition.TOP_LEFT,
                    style: google.maps.MapTypeControlStyle.DROPDOWN_MENU,
                },
                mapTypeId: google.maps.MapTypeId.HYBRID,
                overviewMapControl: true,
                panControl: false,
                scaleControl: false,
                streetViewControl: false,
                zoomControl: true,
            },
            searchbox: {
                events: {
                    // eslint-disable-next-line
                    places_changed: (searchBox: google.maps.places.SearchBox) => {
                        const places = searchBox.getPlaces();

                        if (places.length === 0) {
                            return;
                        }
                        try {
                            const { geometry } = places[0];
                            if (geometry.viewport) {
                                this.googleMap.gMap.fitBounds(geometry.viewport);
                            } else {
                                this.googleMap.gMap.setCenter(geometry.location);
                                this.googleMap.gMap.setZoom(19);
                            }
                        } catch (error) {
                            throw new Error("Search failed, please try again");
                        }
                    },
                },
                options: {
                    // autocomplete: true,
                },
                template: "searchbox.tpl.html",
            },
        };

        this.refreshGoogleMap();
        this.svgRoot = document.querySelector("#mapSvg");
    }

    private refreshGoogleMap() {
        this.$timeout(() => this.googleMap.control.refresh(this.googleMap.map.center), 1000);
        if (this.googleMap.control.getGMap) {
            this.googleMap.gMap = this.googleMap.control.getGMap();
        }
    }

    private calculateGoogleMapScale(): number {
        // This calculates meters per pixel if it's actually correct
        return (
            (156543.03392 * Math.cos((this.googleMap.map.center.latitude * Math.PI) / 180)) /
            // eslint-disable-next-line no-restricted-properties
            Math.pow(2, this.googleMap.gMap.getZoom())
        );
    }
}
